/**
 * ChargeRemindersSection
 *
 * Seção retrátil exibida dentro do `ChargeDetailsModal` que mostra todas as
 * notificações (`reminder_queue`) provisionadas/enviadas/canceladas/falhas
 * para UMA cobrança específica.
 *
 * REAPROVEITAMENTO TOTAL:
 *  - Hook: `useReminderQueueItems` (v1.3.0 — modo entityFilter)
 *  - RPC: `list_reminder_queue_items` (v1.3.0 — filtro por entidade)
 *  - Modal de chat: `ChatConversationModal`
 *  - Paginação: `PaginationFooter`
 *
 * PERFORMANCE:
 *  - Lazy: a query só executa quando o usuário abre o Collapsible
 *  - Server-side pagination (default 10 itens)
 *  - Cache de 30s herdado do hook
 *
 * @version 1.2.0
 * RELEASE: 2026-06-09 v1.0.0 - Criação da seção
 * RELEASE: 2026-09-12 v1.1.0 - Liga/desliga das notificações por cobrança (switch no
 *                              cabeçalho). Persistência via RPC `update_financeiro_cobranca`
 *                              em modo exclusivo de toggle (p_itens NULL + p_reminders_enabled).
 *                              O worker da régua valida o flag NO MOMENTO DO DISPARO.
 * RELEASE: 2026-09-12 v1.2.0 - O switch vira SOMENTE LEITURA. A escolha passa a ser
 *                              feita no modal de criação/edição da cobrança, salva
 *                              junto com os demais campos (uma única gravação atômica).
 *                              Remove a mutação isolada, que enviava campos nulos à
 *                              RPC de edição e falhava (PGRST202).
 */

import React, { useState } from 'react';
import {
  Bell,
  ChevronDown,
  Clock,
  Send,
  AlertCircle,
  Ban,
  SkipForward,
  Loader2,
  ExternalLink,
  CheckCheck,
  Check,
  X,
  Minus,
  ChevronRight,
  Inbox,
  MessageSquare,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { InlineNotice } from '@/components/ui/inline-notice';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { ChatConversationModal } from '@/components/tenant/kanban/ChatConversationModal';
import { useReminderQueueItems } from '@/hooks/useReminderQueueItems';
import { createLogger } from '@/lib/logger';
import { cn } from '@/lib/utils';

// Logger condicional centralizado (padrão do projeto)
const log = createLogger('ChargeRemindersSection');

interface ChargeRemindersSectionProps {
  /** ID da cobrança (entity_id no reminder_queue) */
  cobrancaId: string;
  /** v1.1.0 — flag reminders_enabled da cobrança (vem de get_cobranca_details) */
  remindersEnabled?: boolean;
  /**
   * v1.2.0 — mantido por compatibilidade com o chamador; o switch é somente
   * leitura (a alteração acontece no modal de edição da cobrança).
   */
  canUpdate?: boolean;
}

/** Configuração visual de cada status de envio */
const STATUS_CONFIG: Record<string, {
  label: string;
  icon: React.ElementType;
  textColor: string;
  barColor: string;
}> = {
  pending:    { label: 'Pendente',  icon: Clock,        textColor: 'text-yellow-600',     barColor: 'bg-yellow-500' },
  processing: { label: 'Enviando',  icon: Loader2,      textColor: 'text-blue-600',       barColor: 'bg-blue-500' },
  sent:       { label: 'Enviado',   icon: Send,         textColor: 'text-emerald-600',    barColor: 'bg-emerald-500' },
  failed:     { label: 'Falhou',    icon: AlertCircle,  textColor: 'text-destructive',    barColor: 'bg-destructive' },
  cancelled:  { label: 'Cancelado', icon: Ban,          textColor: 'text-muted-foreground', barColor: 'bg-muted-foreground/40' },
  skipped:    { label: 'Ignorado',  icon: SkipForward,  textColor: 'text-muted-foreground', barColor: 'bg-muted-foreground/40' },
};

/** Status de leitura da mensagem (após envio) */
const READ_STATUS_CONFIG: Record<string, { label: string; icon: React.ElementType; textColor: string }> = {
  read:      { label: 'Lida',     icon: CheckCheck, textColor: 'text-blue-600' },
  delivered: { label: 'Entregue', icon: CheckCheck, textColor: 'text-muted-foreground' },
  sent:      { label: 'Enviada',  icon: Check,      textColor: 'text-muted-foreground' },
  pending:   { label: 'Pendente', icon: Clock,      textColor: 'text-yellow-600' },
  failed:    { label: 'Falhou',   icon: X,          textColor: 'text-destructive' },
};

/** Formata data curta */
const formatDate = (s: string | null) => {
  if (!s) return '—';
  try { return format(new Date(s), "dd/MM/yy HH:mm", { locale: ptBR }); } catch { return '—'; }
};

export function ChargeRemindersSection({
  cobrancaId,
  remindersEnabled = true,
  canUpdate = false,
}: ChargeRemindersSectionProps) {
  // Estado da seção
  const [open, setOpen] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [chatConversationId, setChatConversationId] = useState<string | null>(null);

  // Hook compartilhado — só busca após primeira abertura (performance/lazy)
  const { items, totalCount, isLoading, isFetching } = useReminderQueueItems(
    undefined,
    { status: null, search: null },
    { page, pageSize },
    hasBeenOpened,
    { entityType: 'cobranca', entityId: cobrancaId }
  );

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next && !hasBeenOpened) setHasBeenOpened(true);
  };

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  /** Renderiza status (envio) */
  const renderStatus = (status: string) => {
    const c = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const Icon = c.icon;
    return (
      <div className="flex items-center gap-1 text-[11px] font-medium">
        <Icon className={cn('w-3 h-3', c.textColor, status === 'processing' && 'animate-spin')} />
        <span className="text-foreground">{c.label}</span>
      </div>
    );
  };

  /** Renderiza status de leitura (após envio) */
  const renderReadStatus = (status: string | null) => {
    if (!status) return <Minus className="w-3.5 h-3.5 text-muted-foreground" />;
    const c = READ_STATUS_CONFIG[status] || READ_STATUS_CONFIG.pending;
    const Icon = c.icon;
    return (
      <div className="flex items-center gap-1 text-[11px]">
        <Icon className={cn('w-3 h-3', c.textColor)} />
        <span className="text-foreground">{c.label}</span>
      </div>
    );
  };

  return (
    <>
      <section>
        {/* v1.2.0 — Estado dos lembretes SOMENTE LEITURA. A alteração é feita
            no modal "Editar cobrança" (seção Lembretes automáticos), garantindo
            uma única gravação atômica junto com os demais campos. */}
        <div className="mb-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs text-muted-foreground">Lembretes automáticos</span>
            {!remindersEnabled && (
              <Badge variant="outline" className="text-[10px] h-5 border-amber-500/40 text-amber-600">
                Suspensas
              </Badge>
            )}
            <span className="text-[11px] text-muted-foreground hidden sm:inline">
              Para alterar, use Editar cobrança
            </span>
          </div>
          <Switch
            checked={remindersEnabled}
            disabled
            aria-readonly
            aria-label="Estado das notificações da régua (somente leitura)"
          />
        </div>

        <Collapsible open={open} onOpenChange={handleOpenChange}>
          <CollapsibleTrigger className="w-full flex items-center gap-2 mb-3 group focus:outline-none">
            <Bell className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">
              Notificações da régua de cobrança
            </h3>
            {hasBeenOpened && (
              <Badge variant="secondary" className="text-[10px] h-5">
                {totalCount}
              </Badge>
            )}
            <ChevronDown
              className={cn(
                'h-4 w-4 text-muted-foreground transition-transform ml-auto',
                open && 'rotate-180'
              )}
            />
          </CollapsibleTrigger>

          <CollapsibleContent>
            {/* v1.1.0 — aviso quando a régua está suspensa para esta cobrança */}
            {!remindersEnabled && (
              <InlineNotice variant="warning" className="mb-3">
                Notificações suspensas: a régua não enviará lembretes desta cobrança enquanto estiver desativada.
              </InlineNotice>
            )}
            {isLoading && hasBeenOpened ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                <span className="text-xs">Carregando notificações...</span>
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground gap-2 border border-dashed rounded-lg">
                <Bell className="w-6 h-6 opacity-30" />
                <p className="text-xs">Nenhuma notificação provisionada para esta cobrança.</p>
                <p className="text-[10px] text-muted-foreground/70">
                  Cadastre regras em <strong>Régua de Cobrança</strong> para gerar lembretes automáticos.
                </p>
              </div>
            ) : (
              <>
                <div className="border rounded-lg overflow-hidden bg-background">
                  <div className="overflow-auto max-h-[360px]">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-muted/40 z-[1]">
                        <tr className="border-b border-border/40">
                          <th className="w-[6px] p-0" />
                          <th className="w-[20px] p-0" />
                          <th className="text-[10px] font-medium text-left px-2 py-1.5 text-muted-foreground">Status</th>
                          <th className="text-[10px] font-medium text-left px-2 py-1.5 text-muted-foreground">Contato</th>
                          <th className="text-[10px] font-medium text-left px-2 py-1.5 text-muted-foreground">Agendado</th>
                          <th className="text-[10px] font-medium text-left px-2 py-1.5 text-muted-foreground">Enviado</th>
                          <th className="text-[10px] font-medium text-left px-2 py-1.5 text-muted-foreground">Visualizado</th>
                          <th className="text-[10px] font-medium text-center px-2 py-1.5 text-muted-foreground">Tent.</th>
                          <th className="text-[10px] font-medium text-right px-2 py-1.5 text-muted-foreground w-[50px]">Chat</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item) => {
                          const cfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.pending;
                          const isExpanded = expandedRows.has(item.id);
                          return (
                            <React.Fragment key={item.id}>
                              <tr
                                className="border-b border-border/40 hover:bg-muted/40 cursor-pointer transition-colors"
                                onClick={() => toggleRow(item.id)}
                              >
                                <td className="p-0 w-[6px]">
                                  <div className={cn('w-1 h-full min-h-[36px] rounded-full', cfg.barColor)} />
                                </td>
                                <td className="p-0 w-[20px] text-center">
                                  {isExpanded
                                    ? <ChevronDown className="w-3 h-3 text-muted-foreground inline-block" />
                                    : <ChevronRight className="w-3 h-3 text-muted-foreground inline-block" />}
                                </td>
                                <td className="px-2 py-1.5">{renderStatus(item.status)}</td>
                                <td className="px-2 py-1.5 max-w-[140px]">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="truncate block text-xs font-medium">
                                        {item.contact_name || '—'}
                                      </span>
                                    </TooltipTrigger>
                                    {item.contact_phone && (
                                      <TooltipContent side="top">
                                        <p className="text-xs">{item.contact_phone}</p>
                                      </TooltipContent>
                                    )}
                                  </Tooltip>
                                </td>
                                <td className="px-2 py-1.5 text-xs text-muted-foreground whitespace-nowrap">
                                  {formatDate(item.remind_at)}
                                </td>
                                <td className="px-2 py-1.5 text-xs text-muted-foreground whitespace-nowrap">
                                  {formatDate(item.sent_at)}
                                </td>
                                <td className="px-2 py-1.5 text-xs">
                                  {renderReadStatus(item.message_read_status)}
                                </td>
                                <td className="px-2 py-1.5 text-center text-xs text-muted-foreground">
                                  {item.attempts}
                                </td>
                                <td className="px-2 py-1.5 text-right" onClick={(e) => e.stopPropagation()}>
                                  {item.conversation_id ? (
                                    <Button
                                      variant="ghost"
                                      size="icon-sm"
                                      onClick={() => setChatConversationId(item.conversation_id)}
                                      title="Abrir no chat (kanban)"
                                    >
                                      <ExternalLink className="w-3.5 h-3.5" />
                                    </Button>
                                  ) : (
                                    <span className="text-muted-foreground text-[10px]">—</span>
                                  )}
                                </td>
                              </tr>

                              {/* Linha expandida: detalhes do disparo */}
                              {isExpanded && (
                                <tr className="border-b border-border/40 bg-muted/20">
                                  <td colSpan={9} className="px-3 py-2.5">
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                                      <div className="col-span-2 md:col-span-3">
                                        <span className="text-muted-foreground font-medium block mb-0.5 flex items-center gap-1">
                                          <MessageSquare className="w-3 h-3" />
                                          {item.template_name ? 'Template' : 'Mensagem'}
                                        </span>
                                        <p className="bg-background rounded px-2 py-1.5 border border-border/40 whitespace-pre-wrap text-xs leading-relaxed max-h-[120px] overflow-auto">
                                          {item.template_name || item.message_text || '—'}
                                        </p>
                                      </div>
                                      <div>
                                        <span className="text-muted-foreground font-medium block mb-0.5 flex items-center gap-1">
                                          <Inbox className="w-3 h-3" /> Inbox
                                        </span>
                                        <span>{item.inbox_name || '—'}</span>
                                      </div>
                                      <div>
                                        <span className="text-muted-foreground font-medium block mb-0.5">Criado em</span>
                                        <span>{formatDate(item.created_at)}</span>
                                      </div>
                                      <div>
                                        <span className="text-muted-foreground font-medium block mb-0.5">Telefone</span>
                                        <span className="font-mono text-[11px]">{item.contact_phone || '—'}</span>
                                      </div>
                                      {item.error_details && (
                                        <div className="col-span-2 md:col-span-3">
                                          <span className="text-destructive font-medium block mb-0.5">Erro</span>
                                          <p className="bg-destructive/5 text-destructive rounded px-2 py-1.5 border border-destructive/20 whitespace-pre-wrap text-[11px] max-h-[100px] overflow-auto">
                                            {item.error_details}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Paginação só aparece quando há mais de 1 página */}
                {totalCount > pageSize && (
                  <div className="mt-3">
                    <PaginationFooter
                      totalItems={totalCount}
                      currentPage={page}
                      pageSize={pageSize}
                      onPageChange={setPage}
                      onPageSizeChange={setPageSize}
                      pageSizeOptions={[10, 20, 50]}
                    />
                  </div>
                )}

                {isFetching && !isLoading && (
                  <p className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" /> Atualizando...
                  </p>
                )}
              </>
            )}
          </CollapsibleContent>
        </Collapsible>
      </section>

      {/* Modal de chat — reuso 100% do ChatConversationModal */}
      <ChatConversationModal
        conversationId={chatConversationId}
        open={!!chatConversationId}
        onOpenChange={(o) => { if (!o) setChatConversationId(null); }}
      />
    </>
  );
}
